import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { ARIA_CARD_ACCENT, ARIA_CARD_ACCENT_SOFT } from '../ariaMessageTypes';

/**
 * Onboarding preview body embedded in the message bubble.
 * Intro line comes from message.text — this renders chips, question, and options.
 */
const AriaFirstContactOnboardingPreviewContent = ({
  preview,
  isOutbound = false,
  compact = false,
}) => {
  if (!preview) return null;

  const chips = preview.previewChips ?? [];
  const options = preview.confirmationOptions ?? [];
  const question = preview.confirmationQuestion;
  const hasContent = chips.length > 0 || Boolean(question) || options.length > 0;
  if (!hasContent) return null;

  const secondaryColor = isOutbound ? 'rgba(255,255,255,0.78)' : '#9ca3af';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: compact ? 0.75 : 1,
        width: '100%',
        maxWidth: compact ? '100%' : { sm: 340 },
        minWidth: compact ? 0 : { sm: 300 },
      }}
    >
      {chips.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {chips.map((chip) => (
            <Box
              key={chip}
              sx={{
                display: 'inline-flex',
                alignSelf: 'flex-start',
                px: 1.25,
                py: 0.5,
                borderRadius: '999px',
                bgcolor: isOutbound ? 'rgba(255,255,255,0.14)' : ARIA_CARD_ACCENT_SOFT,
                border: `1px solid ${isOutbound ? 'rgba(255,255,255,0.28)' : `${ARIA_CARD_ACCENT}55`}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: compact ? '0.6875rem' : '0.75rem',
                  fontWeight: 600,
                  color: isOutbound ? '#ffffff' : ARIA_CARD_ACCENT,
                  lineHeight: 1.35,
                }}
              >
                {chip}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : null}

      {question ? (
        <Typography
          sx={{
            fontSize: compact ? '0.75rem' : '0.8125rem',
            color: secondaryColor,
            fontWeight: 500,
            lineHeight: 1.45,
          }}
        >
          {question}
        </Typography>
      ) : null}

      {options.length > 0 ? (
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          {options.map((option, index) => {
            const isPrimary = index === 0;
            return (
              <Button
                key={option.key || option.title}
                disabled
                variant={isPrimary ? 'contained' : 'outlined'}
                sx={{
                  flex: 1,
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: compact ? '0.6875rem' : '0.75rem',
                  lineHeight: 1.35,
                  borderRadius: '12px',
                  py: 0.9,
                  px: 1,
                  minWidth: 0,
                  whiteSpace: 'normal',
                  ...(isPrimary
                    ? {
                        bgcolor: isOutbound ? '#ffffff' : '#374151',
                        color: isOutbound ? '#374151' : '#ffffff',
                        boxShadow: 'none',
                        '&.Mui-disabled': {
                          bgcolor: isOutbound ? 'rgba(255,255,255,0.92)' : '#374151',
                          color: isOutbound ? '#374151' : '#ffffff',
                          opacity: 1,
                        },
                      }
                    : {
                        color: secondaryColor,
                        borderColor: isOutbound ? 'rgba(255,255,255,0.35)' : '#e5e7eb',
                        bgcolor: isOutbound ? 'transparent' : '#ffffff',
                        '&.Mui-disabled': {
                          color: secondaryColor,
                          borderColor: isOutbound ? 'rgba(255,255,255,0.35)' : '#e5e7eb',
                          bgcolor: isOutbound ? 'transparent' : '#ffffff',
                          opacity: 1,
                        },
                      }),
                }}
              >
                {option.title}
              </Button>
            );
          })}
        </Box>
      ) : null}
    </Box>
  );
};

export default AriaFirstContactOnboardingPreviewContent;
